import { useEffect } from 'react';

import { specimenFamily, type FontSpecimen } from './font-specimen';

/**
 * Loads each specimen's font file so a card can draw its sample in the real typeface.
 *
 * Through the CSS Font Loading API rather than an injected `@font-face` rule, for two reasons: a
 * signed url goes into a descriptor the browser parses rather than into a stylesheet this code
 * concatenates, and every face added here is removed again on cleanup. Without that removal,
 * switching folders would leave the document holding faces for fonts no longer on screen, and the
 * set only grows.
 *
 * A file that fails to load is left alone. The card falls back to the interface font, which is a
 * truer signal than an empty card: the font exists, its bytes did not arrive.
 */
export function useFontFaces(specimens: readonly FontSpecimen[]): void {
  // Keyed on the url rather than the array: a re-render that produces an equal list must not drop
  // and re-add every face, which makes the samples flash back to the fallback each time.
  const key = specimens.map((specimen) => `${specimen.id}:${specimen.url ?? ''}`).join('|');

  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) return;

    const loaded: FontFace[] = [];
    let cancelled = false;

    for (const specimen of specimens) {
      if (specimen.url === undefined) continue;

      // The url is a value in a descriptor string, so the quote and backslash that could end it
      // early are the two characters that have to go.
      const src = `url("${specimen.url.replace(/[\\"]/g, '\\$&')}")`;
      let face: FontFace;
      try {
        face = new FontFace(specimenFamily(specimen.id), src);
      } catch {
        continue;
      }

      void face
        .load()
        .then((ready) => {
          if (cancelled) return;
          document.fonts.add(ready);
          loaded.push(ready);
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
      for (const face of loaded) document.fonts.delete(face);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands in for the list's identity
  }, [key]);
}
